import h from 'hyperscript';
import * as is from 'utilities/is';
import EventEmitter from 'eventemitter3';
import { safeJSON, safeStringify, safeSerialize } from './parsers';
import { EXTENSION_API } from 'constants/events';

export const KNOWN_CONTEXTS = ['hapyak'];
export const EVENT_MAP = {
    play: EXTENSION_API.PLAY,
    pause: EXTENSION_API.PAUSE,
    gotoTrack: EXTENSION_API.CURRENT_TIME,
    mute: EXTENSION_API.MUTE,
    unmute: EXTENSION_API.UNMUTE,
    volume: EXTENSION_API.VOLUME,
    addClasses: EXTENSION_API.ADD_CLASSES,
    removeClasses: EXTENSION_API.REMOVE_CLASSES
};

export const PROPERTY_WHITELIST = ['paused', 'duration', 'currentTime', 'isEditMode', 'isEditing', 'isEditMode'];

export const exposeIframeBridge = origin => {
    const bridge = new HapyakIFrameBridge(origin);
    window.addEventListener('message', bridge.messageHandler);
    return bridge;
};

export class HapyakIFrameBridge extends EventEmitter {
    constructor(origin) {
        super();
        this.sourceOrigin = origin;
    }

    get messageHandler() {
        return msg => {
            if (msg && msg.data && this.sourceOrigin.includes(msg.origin)) {
                const message = safeJSON(msg.data);

                if (KNOWN_CONTEXTS.includes(message.context) && EVENT_MAP[message.event]) {
                    this.emit(EVENT_MAP[message.event], message, msg.source);
                }
            }
        };
    }
};

export const sendToIframe = (target, msg) => {
    const payload = safeSerialize({
        context: 'hapyak',
        ...msg
    });

    target && target.postMessage(payload, '*');
};

export const getRemoteIFrameMessage = (src, attrs = {}, timeout = Infinity) => {
    const messageReceiver = iframe => ({
        send: msg => {
            if (iframe && iframe.contentWindow) {
                iframe.contentWindow.postMessage(safeStringify(msg), document.location.origin);
            }
        }
    })

    return new Promise((resolve, reject) => {
        let timeoutId;
        const style = 'position: absolute; width:0; height:0; border:0; border:none',
            iframe = <iframe src={ src } style={ style } { ...attrs } />;

        document.body.appendChild(iframe);

        window.addEventListener('message', event => {
            if (iframe.contentWindow !== event.source) {
                return;
            }

            const data = safeJSON(event.data);

            resolve({ data, messageReceiver: messageReceiver(iframe) });
            window.clearTimeout(timeoutId);
        });

        timeoutId = window.setTimeout(reject, timeout); 
    });
};

export const dummyEventBus = () => {
    const noop = () => {};

    return { on: noop, once: noop, emit: noop, off: noop, onReplay: noop, any: noop };
};

export const onHapyakEvent = handler => event => {
    const msg = safeJSON(event.data);

    if (is.fn(handler) && msg.context === 'hapyak') {
        handler(msg, event);
    }
};

export const makeSerializable = (event, props = []) => {
    event.toJSON = function () {
        return (
            props
                .reduce((serialized, p) => {
                    serialized[p] = this[p];
                    return serialized;
                }, {})
        )
    };

    return event;
}
